/**
 * Typed contract for the phone-verification "Nudge" feature.
 *
 * Source of truth: `apps/api/src/graphql/resolvers/phone-verifications/type.js`
 * (contract-tested against Armageddon's routes by
 * `apps/api/src/graphql/resolvers/phone-verifications/rest-endpoints.test.js`).
 *
 * Every response field on the underlying GraphQL types is nullable on purpose --
 * the Rails controllers legitimately return `null` for `sid` / `phoneHint` /
 * `ownerEmailHint` on several branches. Nothing here should assume presence, and
 * nothing should use non-null assertions on raw API fields.
 *
 * Union members below are intentionally ordered to match the documented
 * contract tables, not alphabetically.
 */
/* eslint-disable @typescript-eslint/sort-type-constituents */

/** `phoneVerificationStatus.status` */
export type PhoneVerificationStatusValue =
  | 'verified_self'
  | 'verified_other'
  | 'unverified'
  | 'nudge_disabled'
  | 'disabled';

/** `sendPhoneVerificationCode.status` / `changePhoneVerificationNumber.status` (same response shape) */
export type PhoneVerificationSendStatus = 'sent' | 'quarantined' | 'not_verifiable' | 'nudge_disabled' | 'disabled';

/** `verifyPhoneVerificationCode.status` */
export type PhoneVerificationCheckStatus =
  | 'verified'
  | 'conflict'
  | 'code_invalid'
  | 'code_expired'
  | 'max_attempts'
  | 'not_verifiable'
  | 'nudge_disabled'
  | 'disabled';

/**
 * Terminal outcome of the Nudge modal flow. This is the value the modal's
 * `openModal` promise resolves with, consumed by the orchestrator that
 * triggers the nudge.
 */
export type NudgeOutcome =
  | { kind: 'verified' }
  | { kind: 'dismissed' }
  | { kind: 'changed_number' }
  | { kind: 'not_verifiable' }
  | { kind: 'login_other_account' };

/** Which leg of the flow a given status response should route to. */
export type NudgeLeg = 'leg_one' | 'leg_two' | 'add_phone' | 'none';

/**
 * `openModal` data for `ModalNames.phoneVerificationNudge`, built by the post-login orchestrator
 * (`use-phone-verification-nudge.ts`) from a single `phoneVerificationNudgeStatus` fetch plus the
 * caller's own `useDispensary()`. The wrapper reuses that request's `dispensaryId` and
 * `enterpriseId` for the mutation calls so the whole flow keeps the same tenant scope.
 * `enterpriseId` is always dispensary-derived -- it never arrives without a `dispensaryId`.
 */
export type PhoneVerificationNudgeContext = {
  initialLeg?: NudgeLeg;
  ownerEmailHint?: string | null;
  dispensaryId?: string | null;
  enterpriseId?: string | null;
};

const STATUS_VALUES: readonly PhoneVerificationStatusValue[] = [
  'verified_self',
  'verified_other',
  'unverified',
  'nudge_disabled',
  'disabled',
];

const SEND_STATUS_VALUES: readonly PhoneVerificationSendStatus[] = [
  'sent',
  'quarantined',
  'not_verifiable',
  'nudge_disabled',
  'disabled',
];

const CHECK_STATUS_VALUES: readonly PhoneVerificationCheckStatus[] = [
  'verified',
  'conflict',
  'code_invalid',
  'code_expired',
  'max_attempts',
  'not_verifiable',
  'nudge_disabled',
  'disabled',
];

/**
 * Parses the raw, nullable `phoneVerificationStatus.status` string into a known
 * union member, or `null` if it's missing/unrecognized. An unrecognized value
 * (e.g. a backend addition the frontend hasn't shipped support for yet) must
 * degrade to a quiet dismiss rather than crash or render a dead-end modal --
 * callers should treat `null` the same as a disabled/verified status (no modal
 * shown).
 */
export function parseStatusValue(raw: string | null | undefined): PhoneVerificationStatusValue | null {
  return (STATUS_VALUES as readonly string[]).includes(raw ?? '') ? (raw as PhoneVerificationStatusValue) : null;
}

/**
 * Parses the raw, nullable send-result status string (shared by
 * `sendPhoneVerificationCode` and `changePhoneVerificationNumber`) into a known
 * union member, or `null` if missing/unrecognized.
 */
export function parseSendStatus(raw: string | null | undefined): PhoneVerificationSendStatus | null {
  return (SEND_STATUS_VALUES as readonly string[]).includes(raw ?? '') ? (raw as PhoneVerificationSendStatus) : null;
}

/**
 * Parses the raw, nullable `verifyPhoneVerificationCode.status` string into a
 * known union member, or `null` if missing/unrecognized.
 */
export function parseCheckStatus(raw: string | null | undefined): PhoneVerificationCheckStatus | null {
  return (CHECK_STATUS_VALUES as readonly string[]).includes(raw ?? '') ? (raw as PhoneVerificationCheckStatus) : null;
}

/**
 * Derives which leg of the Nudge flow a `phoneVerificationStatus` response
 * should route to. `hasPhone` is `boolean | null | undefined` on the wire --
 * anything other than exactly `true` is treated as "no usable phone".
 */
export function deriveNudgeLeg(status: string | null | undefined, hasPhone: boolean | null | undefined): NudgeLeg {
  const parsedStatus = parseStatusValue(status);

  if (parsedStatus === 'verified_other') {
    return 'leg_two';
  }

  if (parsedStatus === 'unverified') {
    return hasPhone === true ? 'leg_one' : 'add_phone';
  }

  // Verified, disabled, and unknown/null statuses all mean "do nothing".
  return 'none';
}
